import { useState, useEffect } from 'react'
import {
  Search,
  FileText,
  Edit,
  Trash2,
  Save,
  Calendar,
  Eye,
  EyeOff,
  X,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'
import { formatDateInTz } from '../../lib/utils/timezone'
import { useInterviewerTimezone } from '../../hooks/useInterviewerTimezone'
import { interviewerApi } from '../../lib/api/interviewer'

interface SessionItem {
  id: number
  client_name: string
  title: string
  scheduled_at: string
  status: string
}

interface Note {
  id: number
  session_id: number
  content: string
  is_private: boolean
  created_at: string
}

export default function InterviewerNotes() {
  const interviewerTz = useInterviewerTimezone()
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [notes, setNotes] = useState<Note[]>([])
  const [search, setSearch] = useState('')
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [noteContent, setNoteContent] = useState('')
  const [isPrivate, setIsPrivate] = useState(true)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [sessionsRes, notesRes] = await Promise.all([
        interviewerApi.getSessions(),
        evaluationsApi.getNotes(),
      ])
      const allSessions = sessionsRes.data || sessionsRes || []
      setSessions(allSessions)
      setNotes(notesRes || [])
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  const filteredSessions = sessions.filter((s) => {
    const name = s.client_name || ''
    return name.toLowerCase().includes(search.toLowerCase())
  })

  const sessionNotes = activeSessionId
    ? notes.filter((n) => n.session_id === activeSessionId)
    : notes

  const handleSelectSession = (sessionId: number) => {
    setActiveSessionId(activeSessionId === sessionId ? null : sessionId)
    setEditingNoteId(null)
    setNoteContent('')
  }

  const handleSaveNote = async () => {
    if (!noteContent.trim()) return
    setSaving(true)
    try {
      if (editingNoteId) {
        await evaluationsApi.updateNote(editingNoteId, {
          content: noteContent,
          is_private: isPrivate,
        })
        setNotes(
          notes.map((n) =>
            n.id === editingNoteId
              ? { ...n, content: noteContent, is_private: isPrivate }
              : n
          )
        )
      } else {
        const newNote = await evaluationsApi.createNote({
          session_id: activeSessionId,
          content: noteContent,
          is_private: isPrivate,
        })
        setNotes([...notes, newNote])
      }
      setNoteContent('')
      setEditingNoteId(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save note.')
    } finally {
      setSaving(false)
    }
  }

  const handleEditNote = (note: Note) => {
    setEditingNoteId(note.id)
    setNoteContent(note.content)
    setIsPrivate(note.is_private)
    setActiveSessionId(note.session_id)
  }

  const handleDeleteNote = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return
    try {
      await evaluationsApi.deleteNote(id)
      setNotes(notes.filter((n) => n.id !== id))
    } catch (err) {
    }
  }

  // Interviewer's timezone, matching the dashboard — not the browser's.
  const formatDate = (dateStr: string) => formatDateInTz(dateStr, interviewerTz)

  if (loading) {
    return <div className="loading-screen">Loading notes...</div>
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Interview Notes</h1>
        <p className="page-subtitle">
          Manage notes across all your interview sessions
        </p>
      </div>

      <div className="in-grid">
        {/* Sessions List */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <Search size={16} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              className="form-input"
              placeholder="Search sessions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 'none', boxShadow: 'none', padding: '6px 0' }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              maxHeight: 500,
              overflowY: 'auto',
            }}
          >
            <button
              onClick={() => {
                setActiveSessionId(null)
                setEditingNoteId(null)
              }}
              style={{
                padding: '12px',
                borderRadius: 8,
                border: 'none',
                background:
                  activeSessionId === null
                    ? 'var(--sidebar-active)'
                    : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                fontWeight: activeSessionId === null ? 600 : 400,
                fontSize: 14,
              }}
            >
              All Notes ({notes.length})
            </button>
            {filteredSessions.map((s) => {
              const count = notes.filter((n) => n.session_id === s.id).length
              return (
                <button
                  key={s.id}
                  onClick={() => handleSelectSession(s.id)}
                  style={{
                    padding: '12px',
                    borderRadius: 8,
                    border: 'none',
                    background:
                      activeSessionId === s.id
                        ? 'var(--sidebar-active)'
                        : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <span
                    style={{
                      fontWeight: activeSessionId === s.id ? 600 : 500,
                      fontSize: 14,
                    }}
                  >
                    {s.client_name || 'Client'}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Calendar size={10} />
                    {formatDate(s.scheduled_at)} | {count} note
                    {count !== 1 ? 's' : ''}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Notes Panel */}
        <div>
          {/* Note Editor */}
          {activeSessionId && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                {editingNoteId ? 'Edit Note' : 'Add Note'}
              </h3>
              <textarea
                className="form-input"
                placeholder="Write your note..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                rows={4}
                style={{ marginBottom: 12 }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                  />
                  {isPrivate ? <EyeOff size={14} /> : <Eye size={14} />}
                  {isPrivate ? 'Private' : 'Shared with client'}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {editingNoteId && (
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '8px 16px', fontSize: 13 }}
                      onClick={() => {
                        setEditingNoteId(null)
                        setNoteContent('')
                      }}
                    >
                      <X size={14} />
                      Cancel
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    style={{ padding: '8px 16px', fontSize: 13 }}
                    onClick={handleSaveNote}
                    disabled={saving || !noteContent.trim()}
                  >
                    <Save size={14} />
                    {saving ? 'Saving...' : editingNoteId ? 'Update' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes List */}
          <div className="card">
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {activeSessionId ? 'Session Notes' : 'All Notes'} (
              {sessionNotes.length})
            </h3>
            {sessionNotes.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <h3>No notes yet</h3>
                <p>
                  {activeSessionId
                    ? 'Add a note for this session above.'
                    : 'Select a session to add notes.'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sessionNotes.map((note) => (
                  <div
                    key={note.id}
                    style={{
                      padding: 16,
                      background: 'var(--accent-light)',
                      borderRadius: 10,
                      borderLeft: note.is_private
                        ? '3px solid var(--warning)'
                        : '3px solid var(--success)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          className={`badge ${note.is_private ? 'badge-warning' : 'badge-success'}`}
                        >
                          {note.is_private ? (
                            <>
                              <EyeOff size={10} /> Private
                            </>
                          ) : (
                            <>
                              <Eye size={10} /> Shared
                            </>
                          )}
                        </span>
                        <span
                          style={{ fontSize: 12, color: 'var(--text-muted)' }}
                        >
                          {formatDate(note.created_at)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={() => handleEditNote(note)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            color: 'var(--text-muted)',
                          }}
                          title="Edit"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            color: 'var(--danger)',
                          }}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <p style={{ fontSize: 14, lineHeight: 1.6 }}>
                      {note.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .in-grid {
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 24px;
          align-items: start;
        }
        @media (max-width: 1024px) {
          .in-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  )
}
